import { validateReservation } from '../schemas/dataSchemas';
import { apiFetch, apiFetchJson } from './dispatchClient';
import { mapVehicleType, mapVehicle, mapResource, mapReservation } from './dispatchMappers';
import { normalizeReservationRecord } from '../lib/reservationNormalizer';
import { debug } from '../lib/debug';
import { getIdByCode, getCodeById } from '../lib/statusMapper';
import type { VehicleType, Vehicle, Resource, Reservation } from '../types';
import { isFixedStaff } from '../utils/staffPriority';
import { SYS_API_BASE } from '../constants/calendar';
import { getDeviceId } from '../services/deviceIdentifier';

const filterTruthy = <T>(list: unknown): T[] => (Array.isArray(list) ? list.filter(Boolean) : []);

// Note: Status constants moved to dynamic master via /api/statuses
// Use getIdByCode / getCodeById for mapping


const toTrimmedString = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  return String(value).trim();
};

const toNullableString = (value: unknown): string | null => {
  const text = toTrimmedString(value);
  return text || null;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === '' || value == null) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

// JST形式（+09:00）で日時文字列を生成
const toIsoString = (value: unknown): string | null => {
  if (!value) return null;

  let date: Date;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    date = value;
  } else {
    const text = toTrimmedString(value);
    if (!text) return null;
    // 既に+09:00形式の場合はそのまま返す
    if (text.includes('+09:00')) {
      return text;
    }
    // パースを試みる
    date = new Date(text);
    if (Number.isNaN(date.getTime())) return text;
  }

  // ローカルタイム（JST環境ではJST）の年月日時分秒を取得して+09:00形式で出力
  // ※サーバー側(v1.14.0)で任意のタイムゾーンをJSTに正規化するため、
  //   ローカルタイムをそのまま送信すればOK
  const pad = (n: number): string => String(n).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+09:00`;
};

const parseDispatchId = (value: unknown): number | null => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
};

const resolveStatusId = (input: unknown): number | null => {
  if (input == null) return null;

  // 1. If numeric, return as ID
  const numeric = Number(input);
  if (Number.isFinite(numeric) && numeric >= 1) {
    return numeric;
  }

  // 2. Try dynamic mapping from statusMapper cache
  const code = toTrimmedString(input).toLowerCase();
  const dynamicId = getIdByCode(code);
  if (dynamicId != null) return dynamicId;

  // No hardcoded fallback here as per user request to use dynamic data only
  return null;
};

const mapReservationResponse = (payload: any): any => {
  // Ensure status_code is present if we have a status ID
  const fixedPayload = { ...payload };
  const statusId = fixedPayload.status ?? fixedPayload.status_id ?? fixedPayload.statusId;
  if (!fixedPayload.status_code && statusId) {
    const code = getCodeById(statusId);
    if (code) {
      fixedPayload.status_code = code;
    }
  }

  const mapped = mapReservation(fixedPayload);
  const normalized = mapped ? normalizeReservationRecord(mapped) : null;
  return normalized || mapped || null;
};

const buildDetailPayload = (input: any = {}): Record<string, any> | null => {
  // Phase 4A: Validate and normalize input
  const normalized = validateReservation(input);
  const inputDetail = (normalized as any).detail; // Guaranteed to be object
  const pick = (key: string): any => input[key] ?? inputDetail[key];
  const pickString = (key: string): string => toNullableString(pick(key)) || '';
  const pickNumber = (key: string): number => {
    const num = toNullableNumber(pick(key));
    return num !== null ? num : 0;
  };
  const pickBool = (key: string): boolean => {
    const val = pick(key);
    return val === true || val === 'true' || val === 1 || val === '1';
  };

  // 有効なデータがあるかチェック
  const hasData = (): boolean => {
    const checkFields = [
      'contact_id', 'contact_media', 'contact_plan',
      'pax', 'pax_children', 'child_seat', 'booster_seat', 'junior_seat',
      'arrival_flight', 'departure_flight',
      'base_price', 'option', 'option_price', 'noc_price', 'cdw_price', 'sum_price',
      'coupon', 'point',
      'invoice_link', 'comment', 'memo',
      'emergency_name', 'emergency_rel', 'emergency_tel',
    ];
    for (const key of checkFields) {
      const val = pick(key);
      if (val !== undefined && val !== null && val !== '' && val !== 0) {
        return true;
      }
    }
    // communication_tools
    const commTools = input.communication_tools ?? inputDetail.communication_tools;
    if (Array.isArray(commTools) && commTools.length > 0) return true;
    // boolean fields
    if (pick('license') === true || pick('license') === 1) return true;
    if (pick('payment') === true || pick('payment') === 1) return true;
    if (pick('terms_and_conditions') === true || pick('terms_and_conditions') === 1) return true;
    if (pick('disclaimer') === true || pick('disclaimer') === 1) return true;
    if (pick('agreement') === true || pick('agreement') === 1) return true;
    if (pick('noc') === true || pick('noc') === 1 || pickBool('noc') === true) return true;
    if (pick('cdw') === true || pick('cdw') === 1 || pickBool('cdw') === true) return true;
    return false;
  };

  // データがない場合はnullを返す（detailを送信しない）
  if (!hasData()) {
    return null;
  }

  // マイグレーション形式: 全フィールドを含める（空文字列や0も含む）
  const commTools = input.communication_tools ?? inputDetail.communication_tools;

  return {
    contact_id: toNullableString(input.contact_id ?? input.contactId ?? inputDetail.contact_id) ?? '',
    contact_media: toNullableString(input.contact_media ?? input.bk ?? inputDetail.contact_media) ?? '',
    contact_plan: pickString('contact_plan'),
    communication_tools: Array.isArray(commTools) ? commTools : [],
    pax: pickNumber('pax'),
    pax_children: pickNumber('pax_children'),
    child_seat: pickNumber('child_seat'),
    booster_seat: pickNumber('booster_seat'),
    junior_seat: pickNumber('junior_seat'),
    arrival_flight: pickString('arrival_flight'),
    departure_flight: pickString('departure_flight'),
    base_price: pickNumber('base_price'),
    option: pickString('option'),
    option_price: pickNumber('option_price'),
    noc: (pick('noc') === true || pick('noc') === 1 || pickNumber('noc_price') > 0),
    noc_price: pickNumber('noc_price'),
    cdw: (pick('cdw') === true || pick('cdw') === 1 || pickNumber('cdw_price') > 0),
    cdw_price: pickNumber('cdw_price'),
    coupon: pickString('coupon'),
    point: pickNumber('point'),
    sum_price: pickNumber('sum_price'),
    days: toNullableNumber(pick('days')),
    nights: toNullableNumber(pick('nights')),
    invoice_link: pickString('invoice_link'),
    comment: pickString('comment'),
    memo: pickString('memo'),
    emergency_name: pickString('emergency_name'),
    emergency_rel: pickString('emergency_rel'),
    emergency_tel: pickString('emergency_tel'),
    license: (pick('license') === true || pick('license') === 1) ? 1 : 0,
    payment: (pick('payment') === true || pick('payment') === 1) ? 1 : 0,
    terms_and_conditions: (pick('terms_and_conditions') === true || pick('terms_and_conditions') === 1) ? 1 : 0,
    disclaimer: (pick('disclaimer') === true || pick('disclaimer') === 1) ? 1 : 0,
    agreement: (pick('agreement') === true || pick('agreement') === 1) ? 1 : 0,
  };
};

// Pick resource_id from input (resource_id is the API field for vehicle assignment)
// resource_id === vehicle_id (same value, legacy naming)
const pickResourceId = (input: any = {}): number | null => {
  const candidates = [
    input.vehicle_id,       // Primary: vehicle_id
    input.vehicleId,
    input.vehicle?.id,
    input.vehicle?.vehicleId,
    input.resource_id,      // Fallback: resource_id (same value, legacy)
    input.resourceId,
    input.resource?.vehicle_id,
    input.resource?.vehicleId,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i];
    const parsed = parseDispatchId(value);
    if (parsed != null) return parsed;
  }
  return null;
};

export const buildDispatchReservationPayload = (input: any = {}): Record<string, any> => {
  if (!input || typeof input !== 'object') {
    throw new Error('reservation input is required');
  }

  const payload: Record<string, any> = {};
  const id = parseDispatchId(input.id ?? input.reservation_id ?? input.dispatchId);
  if (id != null && !String(input.id || '').startsWith('tmp_')) {
    payload.id = id;
  }

  const startDate = toIsoString(input.start_date);
  const endDate = toIsoString(input.end_date);
  if (!startDate) {
    throw new Error('start_date is required to save a reservation');
  }
  if (!endDate) {
    throw new Error('end_date is required to save a reservation');
  }
  payload.start_date = startDate;
  payload.end_date = endDate;

  payload.status = resolveStatusId(input.status ?? input.status_id ?? input.status_code);
  // status_code は API仕様書で「無視されるフィールド」のため送信しない

  const customerName = toNullableString(
    input.customer_name
    ?? input.customerName
    ?? input.cname
    ?? input.contact_name
    ?? input.contactName,
  ) || '予約';
  payload.customer_name = customerName;
  payload.customer_email = toNullableString(input.customer_email ?? input.mail ?? input.customerEmail);
  payload.customer_tel = toNullableString(input.customer_tel ?? input.tel ?? input.customerTel);
  payload.customer_address = toNullableString(input.customer_address ?? input.address);

  // contact_code は指定があれば送信。新規作成時で指定がない場合はバックエンドが自動生成
  const contactCodeCandidate = toNullableString(
    input.contact_code
    ?? input.contactCode
    ?? (String(input.page_id || '').startsWith('tmp_') ? null : input.page_id)
    ?? input.pageId,
  );
  if (contactCodeCandidate) {
    payload.contact_code = contactCodeCandidate.slice(0, 50);
  }

  // car_typeは空文字も送信して、値をクリアできるようにする
  payload.car_type = toTrimmedString(input.car_type);

  // 新規フィールド: 来店希望時間、返却希望時間、直前メモ (v1.14.0で追加)
  payload.short_memo = toTrimmedString(input.short_memo);
  payload.request_coming_time = toTrimmedString(input.request_coming_time);
  payload.request_returning_time = toTrimmedString(input.request_returning_time);

  payload.bg_color = toNullableString(
    input.bg_color ?? input.color_background_hex ?? input.color_bg ?? input.color,
  );
  payload.text_color = toNullableString(
    input.text_color ?? input.color_text_hex ?? input.color_text,
  );

  // booking_date は予約受付日時（新規作成時のみ設定、更新時は送信しない）
  // 更新時（idが存在する場合）は送信しない
  if (!payload.id) {
    const bookingDate = toIsoString(input.booking_date ?? input.bookingDate);
    payload.booking_date = bookingDate || new Date().toISOString();
  }

  // NOTE: vehicle_id is used by the API for vehicle assignment
  // resource_id === vehicle_id (same value, legacy naming)
  // Send ONLY vehicle_id to the API
  // 優先順位: vehicle_id > resourceId（resourceIdはUI内部用）
  const vehicleIdDirect = parseDispatchId(input.vehicle_id ?? input.vehicleId);
  if (vehicleIdDirect != null) {
    payload.vehicle_id = vehicleIdDirect;
  } else {
    // vehicle_id が明示的にない場合のみ pickResourceId を使用
    const resourceId = pickResourceId(input);
    if (resourceId != null) {
      payload.vehicle_id = resourceId;
    }
  }

  // detailを構築（データがない場合はnullが返る）
  const detail = buildDetailPayload(input);
  if (detail !== null) {
    payload.detail = detail;
  }

  // json_dataがあれば追加 (v1.17.0)
  if (input.json_data !== undefined) {
    let jsonData = input.json_data;
    if (typeof jsonData === 'string' && jsonData.trim()) {
      try {
        jsonData = JSON.parse(jsonData);
      } catch (e) {
        // JSON文字列でない場合はそのまま（バリデーションエラーになる可能性が高いが、不適切なパースは避ける）
        console.warn('dispatchService', 'json_data is a string but not valid JSON', e);
      }
    }
    payload.json_data = jsonData;
  }

  // null値、空オブジェクトを除外（バックエンドがnullを正しく処理しない場合に対応）
  const cleanedPayload: Record<string, any> = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    // 空オブジェクトをスキップ
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return;
    cleanedPayload[key] = value;
  });

  return cleanedPayload;
};

export const fetchDispatchVehicleTypes = async (): Promise<VehicleType[]> => {
  const payload = await apiFetchJson('dispatch/vehicle-types');
  const result = Array.isArray(payload) ? payload : [];
  const mapped = filterTruthy<VehicleType>(result.map(mapVehicleType));
  debug.log('dispatchService', 'vehicle types fetched', mapped.length);
  return mapped;
};

export const fetchDispatchVehicles = async (params: Record<string, any> = {}): Promise<Vehicle[]> => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return;
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  const path = query ? `dispatch/vehicles?${query}` : 'dispatch/vehicles';
  const payload = await apiFetchJson(path);
  const result = Array.isArray(payload) ? payload : [];

  // デバッグ: 生データの車両チェック項目を確認
  if (result[0]) {
    // console.log('[fetchDispatchVehicles] 生データサンプル(id=' + result[0].id + '):', {
    //   // チェック項目の可能なフィールド名を確認
    //   cleaning: result[0].cleaning,
    //   washing: result[0].washing,
    //   washer_blade_rear: result[0].washer_blade_rear,
    //   engine_oil_washer: result[0].engine_oil_washer,
    //   tire_tread: result[0].tire_tread,
    //   tire_pressure: result[0].tire_pressure,
    //   // 日本語キーの可能性
    //   '清掃': result[0]['清掃'],
    //   '洗車': result[0]['洗車'],
    //   // completion等
    //   completion: result[0].completion,
    //   detail_completion: result[0].detail_completion,
    //   updated_at: result[0].updated_at,
    // });
  }

  const mapped = filterTruthy<Vehicle>(result.map(mapVehicle));
  debug.log('dispatchService', 'vehicles fetched', mapped.length);
  return mapped;
};

export const fetchDispatchResources = async (params: Record<string, any> = {}): Promise<Resource[]> => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return;
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  const path = query ? `dispatch/resources?${query}` : 'dispatch/resources';
  const payload = await apiFetchJson(path);
  const resources = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.resources)
      ? payload.resources
      : [];
  const mapped = filterTruthy<Resource>(resources.map(mapResource));
  debug.log('dispatchService', 'resources fetched', mapped.length);
  return mapped;
};

const resolveDateParam = (value: unknown): string | null => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  return null;
};

export const fetchDispatchReservations = async (params: Record<string, any> = {}): Promise<Reservation[]> => {
  const searchParams = new URLSearchParams();

  // マッピングしてURLパラメータに追加
  const paramMap: Record<string, string> = {
    startDate: 'start_date',
    endDate: 'end_date',
    status: 'status',
    resourceId: 'resource_id',
    contactCode: 'contact_code',
    contactId: 'contact_id',
    rentalDateFrom: 'rental_date_from',
    rentalDateTo: 'rental_date_to',
    returnDateFrom: 'return_date_from',
    returnDateTo: 'return_date_to',
  };

  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return;
    const apiKey = paramMap[key] || key;

    if (apiKey === 'start_date' || apiKey === 'end_date') {
      const resolved = resolveDateParam(value);
      if (resolved) searchParams.set(apiKey, resolved);
    } else {
      searchParams.set(apiKey, String(value));
    }
  });

  const query = searchParams.toString();
  const path = query ? `dispatch/reservations?${query}` : 'dispatch/reservations';
  const payload = await apiFetchJson(path);

  // Debug: Log raw API response before mapping
  debug.log('dispatchService', 'Raw API response - first 3:', Array.isArray(payload) ? payload.slice(0, 3).map((r: any) => ({
    id: r?.id,
    vehicle_id: r?.vehicle_id,
    resource_id: r?.resource_id,
  })) : payload);

  const result = Array.isArray(payload) ? payload : [];
  const mapped = filterTruthy<Reservation>(result.map(mapReservation));

  // Debug: Log after mapping
  debug.log('dispatchService', 'After mapping - first 3:', mapped.slice(0, 3).map(r => ({
    id: r?.id,
    vehicle_id: r?.vehicle_id,
    vehicleId: r?.vehicleId,
    resource_id: r?.resource_id,
    resourceId: r?.resourceId,
  })));

  debug.log('dispatchService', 'reservations fetched', mapped.length, { query });
  return mapped;
};

export const saveDispatchReservation = async (reservationInput: any = {}): Promise<Reservation | null> => {
  const payload = buildDispatchReservationPayload(reservationInput);
  debug.log('dispatchService', 'saveDispatchReservation payload:', payload);

  // DEBUG: 送信ペイロードをコンソールに出力
  // console.log('=== SAVE RESERVATION PAYLOAD ===');
  // console.log(JSON.stringify(payload, null, 2));
  // console.log('================================');

  const response = await apiFetchJson('dispatch/reservations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  debug.log('dispatchService', 'saveDispatchReservation response:', response);

  const mapped = mapReservationResponse(response);
  debug.log('dispatchService', 'reservation saved', { id: mapped?.id, payload });
  return mapped;
};

export const deleteDispatchReservation = async (reservationId: string | number): Promise<void> => {
  const id = parseDispatchId(reservationId);
  if (id == null) {
    throw new Error('reservationId is required to delete a reservation');
  }
  await apiFetch(`dispatch/reservations/${id}`, { method: 'DELETE' });
  debug.log('dispatchService', 'reservation deleted', { id });
};

// スタッフ一覧を取得 (GET /api/staff)
export const fetchStaffList = async (params: Record<string, any> = {}): Promise<any[]> => {
  const searchParams = new URLSearchParams();
  const { active, level, name, per_page } = params;

  // デフォルトで在籍中スタッフのみを取得
  if (active !== undefined) searchParams.set('active', String(active));
  if (level !== undefined) searchParams.set('level', String(level));
  if (name) searchParams.set('name', name);
  if (per_page) searchParams.set('per_page', String(per_page));

  const query = searchParams.toString();
  const path = query ? `staff?${query}` : 'staff';

  try {
    const payload = await apiFetchJson(path);
    // APIはページネーション形式で返す
    const result = Array.isArray(payload) ? payload : (payload?.data || []);
    debug.log('dispatchService', 'staff list fetched', result.length);
    return result;
  } catch (error) {
    console.error('[dispatchService] fetchStaffList error:', error);
    throw error;
  }
};

// --- スタッフ優先度管理（localStorage + BroadcastChannel同期） ---
// ※ バックエンドAPI (PUT staff/{id}) は管理者権限が必要なため、localStorageで管理
const STAFF_PRIORITY_KEY = 'staff_priorities';
const STAFF_PRIORITY_CHANNEL = 'staff-priority-sync';

// BroadcastChannel（タブ間同期）
let _priorityChannel: BroadcastChannel | null = null;
const getPriorityChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!_priorityChannel) {
    _priorityChannel = new BroadcastChannel(STAFF_PRIORITY_CHANNEL);
  }
  return _priorityChannel;
};

/** 単一スタッフの優先度を更新 */
export const updateStaffPriority = async (staffId: number, priority: number): Promise<any> => {
  saveStaffPriorityLocal(staffId, priority);
  return { id: staffId, priority };
};

/** 一括保存 + 全タブ（BroadcastChannel）+ 全デバイス（Pusher）へブロードキャスト */
export const saveStaffPrioritiesBulk = (updates: { id: number; priority: number }[]): void => {
  const stored = JSON.parse(localStorage.getItem(STAFF_PRIORITY_KEY) || '{}');
  updates.forEach(u => { stored[String(u.id)] = u.priority; });
  localStorage.setItem(STAFF_PRIORITY_KEY, JSON.stringify(stored));
  // 他タブに通知
  getPriorityChannel()?.postMessage({ type: 'staff_priority_updated', priorities: stored });
  // 同一タブ内にも通知（BroadcastChannelは送信元タブには届かないため）
  window.dispatchEvent(new CustomEvent('staff-priority-updated', { detail: stored }));
  // 他デバイスに Pusher 経由で通知（fire-and-forget）
  try {
    fetch(`${SYS_API_BASE}/staff/broadcast_priority.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ priorities: stored, device_id: getDeviceId() }),
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
};

export const saveStaffPriorityLocal = (staffId: number, priority: number): void => {
  const stored = JSON.parse(localStorage.getItem(STAFF_PRIORITY_KEY) || '{}');
  stored[String(staffId)] = priority;
  localStorage.setItem(STAFF_PRIORITY_KEY, JSON.stringify(stored));
};

/** localStorageのpriority情報をスタッフリストにマージ */
export const mergeStaffPriorities = (staffList: any[]): any[] => {
  const stored = JSON.parse(localStorage.getItem(STAFF_PRIORITY_KEY) || '{}');
  return staffList.map(s => {
    const localPriority = stored[String(s.id)];
    let priority = localPriority !== undefined ? localPriority : s.priority;
    // 固定スタッフ（staff / スタッフ / タイミー）は常に本日出勤扱い
    if (isFixedStaff(s)) {
      const base = typeof priority === 'number' ? (priority % 1000) : 0;
      priority = base + 1000;
    }
    return priority !== undefined ? { ...s, priority } : s;
  });
};

/** 優先度変更リスナーを登録（タブ間 + 同一タブ内）。返り値のcleanupを呼ぶと解除 */
export const onStaffPriorityChange = (callback: () => void): (() => void) => {
  // BroadcastChannel（他タブからの通知）
  const ch = getPriorityChannel();
  const bcHandler = (e: MessageEvent) => {
    if (e.data?.type === 'staff_priority_updated') callback();
  };
  ch?.addEventListener('message', bcHandler);

  // CustomEvent（同一タブからの通知）
  const ceHandler = () => callback();
  window.addEventListener('staff-priority-updated', ceHandler);

  return () => {
    ch?.removeEventListener('message', bcHandler);
    window.removeEventListener('staff-priority-updated', ceHandler);
  };
};

// 予約にスタッフを割り当て保存
export const saveReservationStaff = async (reservation: any, staffField: string, staffId: number | null): Promise<Reservation | null> => {
  // reservation: 予約オブジェクト全体
  // staffField: pu_staff_id, do_staff_id, rent_staff_id, return_staff_id
  // staffId: スタッフID（nullの場合は解除）

  const id = parseDispatchId(reservation.id);
  if (id == null) {
    throw new Error('reservationId is required');
  }

  // 既存のスタッフIDを取得（reservationオブジェクトはpu_staff, do_staff等のオブジェクト形式）
  const existingPuStaffId = reservation.pu_staff?.id || null;
  const existingDoStaffId = reservation.do_staff?.id || null;
  const existingRentStaffId = reservation.rent_staff?.id || null;
  const existingReturnStaffId = reservation.return_staff?.id || null;

  // 必須フィールドとすべてのスタッフフィールドを含むペイロードを構築
  // 他のスタッフフィールドを保持しつつ、指定されたフィールドのみ更新
  const payload = {
    id,
    start_date: reservation.start_date,
    end_date: reservation.end_date,
    status: resolveStatusId(reservation.status),
    customer_name: reservation.customer_name || '予約',
    pu_staff_id: staffField === 'pu_staff_id' ? staffId : existingPuStaffId,
    do_staff_id: staffField === 'do_staff_id' ? staffId : existingDoStaffId,
    rent_staff_id: staffField === 'rent_staff_id' ? staffId : existingRentStaffId,
    return_staff_id: staffField === 'return_staff_id' ? staffId : existingReturnStaffId,
  };

  debug.log('dispatchService', 'saveReservationStaff payload:', payload);

  const response = await apiFetchJson('dispatch/reservations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  debug.log('dispatchService', 'saveReservationStaff response:', response);

  const mapped = mapReservationResponse(response);
  debug.log('dispatchService', 'reservation staff saved', { id: mapped?.id, staffField, staffId });
  return mapped;
};

// 予約の免許確認状態を更新
export const toggleReservationLicense = async (reservation: any, licenseValue: number): Promise<Reservation | null> => {
  // reservation: 予約オブジェクト全体
  // licenseValue: 0 (未確認) or 1 (確認済)

  const id = parseDispatchId(reservation.id);
  if (id == null) {
    throw new Error('reservationId is required');
  }

  // 必須フィールドを含むペイロードを構築（条件付き更新のためdetail.licenseを明示的に送信）
  const payload = {
    id,
    start_date: reservation.start_date,
    end_date: reservation.end_date,
    status: resolveStatusId(reservation.status),
    customer_name: reservation.customer_name || '予約',
    detail: {
      license: licenseValue,
    },
  };

  debug.log('dispatchService', 'toggleReservationLicense payload:', payload);

  const response = await apiFetchJson('dispatch/reservations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  debug.log('dispatchService', 'toggleReservationLicense response:', response);

  const mapped = mapReservationResponse(response);
  debug.log('dispatchService', 'reservation license toggled', { id: mapped?.id, license: licenseValue });
  return mapped;
};

// 予約の連絡手段を更新
export const updateReservationCommunicationTools = async (reservation: any, communicationTools: number[]): Promise<Reservation | null> => {
  // reservation: 予約オブジェクト全体
  // communicationTools: 連絡手段ID配列 [1, 2, 3, ...]

  const id = parseDispatchId(reservation.id);
  if (id == null) {
    throw new Error('reservationId is required');
  }

  // 必須フィールドを含むペイロードを構築
  const payload = {
    id,
    start_date: reservation.start_date,
    end_date: reservation.end_date,
    status: resolveStatusId(reservation.status),
    customer_name: reservation.customer_name || '予約',
    detail: {
      communication_tools: communicationTools,
    },
  };

  debug.log('dispatchService', 'updateReservationCommunicationTools payload:', payload);

  const response = await apiFetchJson('dispatch/reservations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  debug.log('dispatchService', 'updateReservationCommunicationTools response:', response);

  const mapped = mapReservationResponse(response);
  debug.log('dispatchService', 'reservation communication tools updated', { id: mapped?.id, tools: communicationTools });
  return mapped;
};

export const updateVehicle = async (vehicleData: any = {}): Promise<Vehicle | null> => {
  const { id } = vehicleData;
  if (!id) {
    throw new Error('Vehicle ID is required for update');
  }

  // API仕様書 v1.2.0に基づき、更新可能なフィールドのみを抽出
  const allowedFields = [
    'status',
    'location_status',  // v1.1.0で追加
    'memo',
    'cleaning',
    'car_wash',
    'check1',
    'check2',
    'tire_groove',
    'tire_pressure',
    'active_from',
    'active_to',
    'is_active',        // v1.2.0で追加: 有効フラグ
    'reservation_box_priority',
    'check_situations', // 配列形式のチェック項目
    'out_of_use',       // v1.4.0で追加
    'json_data',        // v1.5.0で追加
  ];

  const payload: Record<string, any> = {
    id: Number(id),
  };

  allowedFields.forEach(field => {
    if (vehicleData[field] !== undefined) {
      payload[field] = vehicleData[field];
    }
  });

  // statusは数値型であることを保証
  if (payload.status !== undefined) {
    payload.status = Number(payload.status);
  }

  // location_statusはnullまたは数値型
  if (payload.location_status !== undefined && payload.location_status !== null) {
    payload.location_status = Number(payload.location_status);
  }

  // out_of_useはboolean型であることを保証
  if (payload.out_of_use !== undefined) {
    payload.out_of_use = Boolean(payload.out_of_use);
  }

  // json_dataがオブジェクトであることを保証（文字列の場合はパース）
  if (payload.json_data !== undefined && typeof payload.json_data === 'string' && payload.json_data.trim()) {
    try {
      payload.json_data = JSON.parse(payload.json_data);
    } catch (e) {
      console.warn('dispatchService', 'vehicle json_data is not valid JSON string', e);
    }
  }

  // priorityフィールドをreservation_box_priorityに変換 (後方互換性)
  if (vehicleData.priority !== undefined && payload.reservation_box_priority === undefined) {
    payload.reservation_box_priority = vehicleData.priority;
  }

  debug.log('dispatchService', 'updateVehicle payload:', payload);

  const response = await apiFetchJson('dispatch/vehicles/update', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  debug.log('dispatchService', 'updateVehicle response:', response);

  const mapped = mapVehicle(response);
  return mapped;
};

// Square Invoices APIを一括取得（ページネーション・フィルタ用）
export const fetchSquareInvoicesBulk = async (params: Record<string, any> = {}): Promise<any> => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  const path = query ? `admin/square-invoices?${query}` : 'admin/square-invoices';

  try {
    const response = await apiFetchJson(path);
    debug.log('dispatchService', 'fetchSquareInvoicesBulk response count:', response?.data?.length);
    return response;
  } catch (error) {
    console.error('[dispatchService] fetchSquareInvoicesBulk error:', error);
    throw error;
  }
};

// Square Invoices API呼び出し
// contact_idで検索してInvoice情報を取得
export const fetchSquareInvoicesByContactId = async (contactId: string): Promise<any> => {
  if (!contactId) {
    return { data: [], meta: { total_count: 0 } };
  }

  try {
    // APIは contact_id パラメータで部分一致検索をサポート
    const response = await apiFetchJson(`admin/square-invoices?contact_id=${encodeURIComponent(contactId)}&per_page=100`);
    debug.log('dispatchService', 'fetchSquareInvoicesByContactId response:', response);
    return response;
  } catch (error) {
    console.error('[dispatchService] fetchSquareInvoicesByContactId error:', error);
    throw error;
  }
};

// 複数のcontact_idでSquare Invoicesを一括取得
export const fetchSquareInvoicesForReservations = async (contactIds: string[]): Promise<Record<string, any[]>> => {
  if (!Array.isArray(contactIds) || contactIds.length === 0) {
    return {};
  }

  // 重複を除去
  const uniqueContactIds = [...new Set(contactIds.filter(Boolean))];

  // 結果を contact_id -> invoices[] のマップで返す
  const resultMap: Record<string, any[]> = {};

  // 並列で取得（API負荷を考慮して5件ずつ）
  const batchSize = 5;
  for (let i = 0; i < uniqueContactIds.length; i += batchSize) {
    const batch = uniqueContactIds.slice(i, i + batchSize);
    const promises = batch.map(async (contactId) => {
      try {
        const result = await fetchSquareInvoicesByContactId(contactId);
        resultMap[contactId] = result.data || [];
      } catch (error) {
        console.error(`[dispatchService] Failed to fetch invoices for ${contactId}:`, error);
        resultMap[contactId] = [];
      }
    });
    await Promise.all(promises);
  }

  debug.log('dispatchService', 'fetchSquareInvoicesForReservations result:', Object.keys(resultMap).length, 'contacts');
  return resultMap;
};

/**
 * 車両入れ替えAPI
 * 2台の車両のデータを完全に入れ替える
 */
export const swapVehicles = async (vehicleIdA: number | string, vehicleIdB: number | string): Promise<any> => {
  const idA = parseDispatchId(vehicleIdA);
  const idB = parseDispatchId(vehicleIdB);

  if (!idA || !idB) {
    throw new Error('有効な車両IDを指定してください');
  }

  if (idA === idB) {
    throw new Error('同じ車両を入れ替えることはできません');
  }

  debug.log('dispatchService', 'swapVehicles', { vehicleIdA: idA, vehicleIdB: idB });

  const response = await apiFetchJson('dispatch/vehicles/swap', {
    method: 'POST',
    body: JSON.stringify({
      vehicle_id_a: idA,
      vehicle_id_b: idB,
    }),
  });

  debug.log('dispatchService', 'swapVehicles response', response);
  return response;
};

/**
 * ステータスマスターを一括取得
 */
export const fetchStatuses = async (): Promise<any> => {
  debug.log('dispatchService', 'fetchStatuses');

  const response = await apiFetchJson('statuses');

  debug.log('dispatchService', 'fetchStatuses response', response);
  return response;
};

/**
 * 車両チェック項目マスターを取得
 */
export const fetchVehicleChecks = async (): Promise<any[]> => {
  debug.log('dispatchService', 'fetchVehicleChecks');

  const response = await apiFetchJson('vehicle-checks');
  const result = Array.isArray(response) ? response : [];

  debug.log('dispatchService', 'fetchVehicleChecks response', result.length);
  return result;
};
